/**
 * 指定フォーマット出力（#3070）のフォーマット管理セクション。
 *
 * 読取パターン編集画面の「設定・保存」ステップに置き、
 * 事務局がフォーマット（.xlsx）を登録・削除する。
 *
 * 登録の流れ:
 *   1. .xlsx を選ぶ
 *   2. シート一覧と使用中フィールドコードを解析して表示
 *   3. 埋め込み対象シートを選ぶ（使い回しの残骸シートを除外するため。仕様書 §3.7）
 *   4. 登録
 */
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UndoIcon from '@mui/icons-material/Undo';
import PatternFileUploader from '../form/PatternFileUploader';

import { gray40, gray60, gray80, grayBlue50, orange50, red50 } from '../../../constants/colors';
import { EXPORT_TEMPLATE_MAX_FILE_SIZE } from '../../../constants/enum';
import { type ExportTemplate, type ExportTemplateAnalysis } from '../../../types/exportTemplate';
import { type ItemListType } from '../../../types/common';
import { analyzeExportTemplate } from '../../../api/firebase/function/exportTemplateFunction';
import { collectFieldCodes, fillMissingFieldCodes } from '../../../utils/pattern/fieldCode';
import {
  getExportTemplates,
  getExportTemplateFile,
} from '../../../api/firebase/firestore/exportTemplate';

type Props = {
  /** 編集中のパターン。未保存（新規作成中）なら undefined */
  patternId?: string;
  companyId: string;
  /** フィールドコードの照合に使う項目定義 */
  itemList: ItemListType[];
  onShowMessage?: (message: string) => void;
  /**
   * 未保存の変更を親へ通知する。パターンの保存時にまとめて反映するため、
   * この画面では Firestore / Storage に書き込まない（他の設定項目と挙動を揃える）。
   */
  onChange?: (pending: PendingExportTemplateChanges) => void;
};

/** パターン保存時にまとめて反映する変更内容 */
export type PendingExportTemplateChanges = {
  /** 追加するフォーマット（ファイル本体を保持したまま保存時にアップロードする） */
  added: {
    file: File;
    templateName: string;
    targetSheets: string[];
    allSheets: string[];
    usedFieldCodes: string[];
    /** 画面上で識別するための一時ID */
    tempId: string;
  }[];
  /** 削除する登録済みフォーマットのID */
  removedIds: string[];
  /**
   * ファイルを差し替える登録済みフォーマット。
   * ドキュメントIDを保ったまま中身だけを入れ替えるため、
   * 既定フォーマットの設定など将来 ID を参照する機能が壊れない。
   */
  replaced: {
    id: string;
    file: File;
    templateName: string;
    targetSheets: string[];
    allSheets: string[];
    usedFieldCodes: string[];
  }[];
  /** 対象シートを変更した登録済みフォーマット */
  updatedSheets: { id: string; targetSheets: string[] }[];
};

/**
 * フォーマットが参照しているフィールドコードのうち、
 * 読取パターンに存在しないものを返す（出力時に空欄になることを事前に知らせる）。
 */
const findUnknownFieldCodes = (
  analysis: ExportTemplateAnalysis,
  available: ReadonlySet<string>,
): string[] => {
  const unknown = new Set<string>();
  analysis.fieldCodes.forEach(code => {
    if (!available.has(code)) unknown.add(code);
  });
  analysis.rowsNames.forEach(name => {
    if (!available.has(name)) unknown.add(name);
  });
  return Array.from(unknown);
};

/** File を Base64 に変換する（Functions へ渡すため） */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const result = String(reader.result);
      // "data:...;base64,XXXX" の XXXX 部分だけを取り出す
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = (): void => reject(reader.error);
    reader.readAsDataURL(file);
  });

const styles = {
  // 「自動仕分の対象にする」など既存セクションと同じ体裁に揃える。
  // 親（設定・保存ステップ）が中央寄せのため、ここで左寄せに戻す
  section: { textAlign: 'left' as const },
  title: {
    color: grayBlue50,
    fontSize: '16px',
    fontWeight: 700,
    marginBottom: '8px',
  },
  description: { fontSize: '12px', color: gray80, lineHeight: '1.8', marginBottom: '12px' },
  card: {
    border: `1px solid ${gray40}`,
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '12px 0',
    borderBottom: `1px solid ${gray40}`,
  },
  error: { fontSize: '12px', color: red50, marginTop: '6px', lineHeight: '1.8' },
};

const ExportTemplateManager = ({
  patternId,
  companyId,
  itemList,
  onShowMessage,
  onChange,
}: Props): ReactElement => {
  const [templates, setTemplates] = useState<ExportTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // パターン保存時にまとめて反映する未保存の変更（この画面では書き込まない）
  const [pending, setPending] = useState<PendingExportTemplateChanges>({
    added: [],
    removedIds: [],
    replaced: [],
    updatedSheets: [],
  });

  /** 変更を記録する（親への通知は下の useEffect でレンダリング後に行う） */
  const applyPending = (
    updater: (prev: PendingExportTemplateChanges) => PendingExportTemplateChanges,
  ): void => {
    setPending(updater);
  };

  // 親への通知はレンダリング後に行う。setPending の updater の中で呼ぶと
  // 「レンダリング中に別コンポーネントを更新した」warning になるため（#3207）。
  // onChange は親で毎レンダー再生成されるため ref に逃がし、pending の変化だけで通知する。
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // 初回マウント時は通知しない（未変更の状態を親へ流さない）
  const notifiedPendingRef = useRef(false);
  useEffect(() => {
    if (!notifiedPendingRef.current) {
      notifiedPendingRef.current = true;
      return;
    }
    onChangeRef.current?.(pending);
  }, [pending]);

  const loadTemplates = useCallback(async (): Promise<void> => {
    if (!patternId) return;
    try {
      setTemplates(await getExportTemplates(patternId, companyId));
      setError(null);
    } catch (e) {
      // 取得できないこと自体は登録の妨げにならないため、一覧を空にして続行する。
      // 権限エラーはルール未反映（デプロイ漏れ）のことが多いので、原因を出し分ける
      const message =
        e instanceof Error && e.message.includes('permission')
          ? 'フォーマットを取得する権限がありません。設定の反映状況をご確認ください。'
          : 'フォーマットの取得に失敗しました。';
      setTemplates([]);
      setError(message);
    }
  }, [patternId, companyId]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  /** 選択されたファイルを解析して、シート一覧と参照コードを出す */
  /**
   * ファイルを選ぶ。差し替え対象が指定されていればそのフォーマットを差し替え、
   * 指定が無ければ新規追加として積む。
   */
  const handleFileSelect = async (
    selected: File,
    replaceTarget?: ExportTemplate,
  ): Promise<void> => {
    setError(null);
    if (!selected.name.toLowerCase().endsWith('.xlsx')) {
      setError('.xlsx ファイルを選択してください。');
      return;
    }
    if (selected.size > EXPORT_TEMPLATE_MAX_FILE_SIZE) {
      setError(
        `ファイルサイズが大きすぎます（上限 ${EXPORT_TEMPLATE_MAX_FILE_SIZE / 1024 / 1024}MB）。`,
      );
      return;
    }
    // 解析と登録で数秒かかるため、選択直後から処理中を示す
    setLoading(true);
    try {
      // 解析はサーバ側で行う（xlsx-template が Node 依存のため）
      const base64 = await fileToBase64(selected);
      const result = await analyzeExportTemplate({ file_content: base64 });
      // 未保存パターンでも参照できるよう、フィールドコードは補完してから照合する
      const available = collectFieldCodes(fillMissingFieldCodes(itemList));
      const unknown = findUnknownFieldCodes(result, available);
      if (unknown.length > 0) {
        onShowMessage?.(
          `次の項目は読取パターンに存在しません。出力時は空欄になります: ${unknown.join('、')}`,
        );
      }
      // 選択した時点で記録する（登録ボタンは設けず、一覧に反映する）
      if (replaceTarget) {
        stageReplace(replaceTarget, selected, result.sheetNames, result.fieldCodes);
      } else {
        stageTemplate(selected, result.sheetNames, result.fieldCodes);
      }
    } catch {
      setError(
        'ファイルを読み込めませんでした。Excel で保存した .xlsx ファイルか確認してください。',
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * チェックボックスに並べるシート名。
   * 差し替え予定があるときは新しいファイルのシート構成を見せる（古いデータには
   * all_sheets が無いため、その場合は対象シートで代替する）
   */
  const sheetNamesOf = (t: ExportTemplate): string[] => {
    const replace = pending.replaced.find(r => r.id === t.id);
    if (replace) return replace.allSheets;
    return t.all_sheets?.length ? t.all_sheets : t.target_sheets;
  };

  /**
   * 選択したフォーマットを未保存の追加分として積む。
   * ファイル本体は保持しておき、パターン保存時にアップロードする。
   */
  const stageTemplate = (selected: File, sheetNames: string[], fieldCodes: string[]): void => {
    applyPending(prev => ({
      ...prev,
      added: [
        ...prev.added,
        {
          file: selected,
          templateName: selected.name.replace(/\.xlsx$/i, ''),
          targetSheets: sheetNames,
          allSheets: sheetNames,
          usedFieldCodes: fieldCodes,
          tempId: `${Date.now()}_${selected.name}`,
        },
      ],
    }));
  };

  /** 手元のファイル／取得済みのデータをダウンロードさせる */
  const saveAsFile = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  /**
   * 登録中のフォーマットをダウンロードする。
   * 未保存の追加・差し替えはまだ Storage に無いため、手元のファイルをそのまま渡す。
   */
  const downloadTemplate = async (t: ExportTemplate): Promise<void> => {
    const replace = pendingReplaceOf(t);
    if (replace) {
      saveAsFile(replace.file, replace.file.name);
      return;
    }
    try {
      const bytes = await getExportTemplateFile(t.file_path);
      // Uint8Array をそのまま渡すと SharedArrayBuffer の可能性が残り型が合わないため、
      // 実体を持つ ArrayBuffer に写してから Blob にする
      const body = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(body).set(bytes);
      saveAsFile(new Blob([body]), t.file_name);
    } catch {
      onShowMessage?.('フォーマットをダウンロードできませんでした');
    }
  };

  /** 登録済みを削除対象として記録する（実際の削除はパターン保存時） */
  const stageRemove = (templateId: string): void => {
    applyPending(prev => ({ ...prev, removedIds: [...prev.removedIds, templateId] }));
  };

  /** 登録済みのファイルを差し替える（実際の反映はパターン保存時） */
  const stageReplace = (
    template: ExportTemplate,
    selected: File,
    sheetNames: string[],
    fieldCodes: string[],
  ): void => {
    if (!template.id) return;
    const id = template.id;
    applyPending(prev => ({
      ...prev,
      replaced: [
        ...prev.replaced.filter(r => r.id !== id),
        {
          id,
          file: selected,
          templateName: selected.name.replace(/\.xlsx$/i, ''),
          targetSheets: sheetNames,
          allSheets: sheetNames,
          usedFieldCodes: fieldCodes,
        },
      ],
      // 差し替えたらシート変更の記録は不要（新しいファイルの全シートが対象になる）
      updatedSheets: prev.updatedSheets.filter(u => u.id !== id),
    }));
  };

  /** 差し替えを取り消す */
  const unstageReplace = (templateId: string): void => {
    applyPending(prev => ({ ...prev, replaced: prev.replaced.filter(r => r.id !== templateId) }));
  };

  /** 未保存の追加分を取り消す */
  const unstageAdded = (tempId: string): void => {
    applyPending(prev => ({ ...prev, added: prev.added.filter(a => a.tempId !== tempId) }));
  };

  /** 対象シートの変更を記録する（実際の保存はパターン保存時） */
  const stageSheetChange = (templateId: string, targetSheets: string[]): void => {
    // 全部外すと出力先が無くなるため、最低1つは残す
    if (targetSheets.length === 0) return;
    applyPending(prev => {
      // 差し替え予定があるときは、そちらの対象シートを更新する。
      // updatedSheets に積んでも差し替え内容で上書きされてしまうため
      if (prev.replaced.some(r => r.id === templateId)) {
        return {
          ...prev,
          replaced: prev.replaced.map(r => (r.id === templateId ? { ...r, targetSheets } : r)),
        };
      }
      return {
        ...prev,
        updatedSheets: [
          ...prev.updatedSheets.filter(u => u.id !== templateId),
          { id: templateId, targetSheets },
        ],
      };
    });
  };

  /** この登録済みフォーマットに差し替え予定があれば返す */
  const pendingReplaceOf = (
    t: ExportTemplate,
  ): PendingExportTemplateChanges['replaced'][number] | undefined =>
    pending.replaced.find(r => r.id === t.id);

  /** 画面に出す対象シート（未保存の変更があればそれを優先する） */
  const effectiveSheets = (t: ExportTemplate): string[] => {
    const replace = pending.replaced.find(r => r.id === t.id);
    if (replace) return replace.targetSheets;
    return pending.updatedSheets.find(u => u.id === t.id)?.targetSheets ?? t.target_sheets;
  };

  /** 削除予定を除いた登録済み一覧 */
  const visibleTemplates = templates.filter(t => t.id && !pending.removedIds.includes(t.id));

  return (
    <Box sx={styles.section}>
      <Typography sx={styles.title}>指定フォーマット出力（任意）</Typography>
      <Typography sx={styles.description}>
        書式付きの Excel を登録すると、OCR結果をその書式に流し込んで出力できます。
        値を入れたいセルに <code>{'${フィールドコード}'}</code>、明細の開始行に{' '}
        <code>{'${rows:明細}'}</code> を記入してください。
      </Typography>

      {/* 未保存のパターンには紐づけられないため、保存を促す */}
      {!patternId && (
        <Typography sx={styles.description}>
          読取パターンを保存すると、フォーマットを登録できます。
        </Typography>
      )}

      {/* アップロード。選択した時点で登録し、下の一覧に積んでいく */}
      {patternId && (
        <Box>
          {/* パターン作成時のアップロードと同じ部品・デザインに揃える */}
          <PatternFileUploader
            labelText=""
            requiredText=""
            helperText={undefined}
            height="140px"
            multiple={false}
            accept=".xlsx"
            disabled={loading}
            onFilesSelected={(files: FileList | null): void => {
              const selected = files?.[0];
              if (selected) void handleFileSelect(selected);
            }}
          />

          {/* 解析（サーバ側）とアップロードで数秒かかるため、進行中を明示する */}
          {loading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '12px',
              }}
            >
              <CircularProgress size={16} sx={{ color: orange50 }} />
              <Typography sx={{ fontSize: '14px', color: gray80 }}>
                ファイルを確認しています…
              </Typography>
            </Box>
          )}

          {error && <Typography sx={styles.error}>{error}</Typography>}

          {/* 登録済みのフォーマット（STEP1 のファイル一覧と同じ見せ方）。
              行を開くと、書き込む対象のシートを変更できる */}
          {visibleTemplates.map(t => (
            <Accordion
              key={t.id}
              // 登録直後に対象シートを確認・調整できるよう、初期状態は開いておく
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                '&::before': { display: 'none' },
                backgroundColor: 'transparent',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                // 既定の <button> だと、中に置く差し替え・削除ボタンが
                // button の入れ子になって DOM として不正になるため div にする
                component="div"
                sx={{
                  padding: 0,
                  borderBottom: `1px solid ${gray40}`,
                  minHeight: '48px',
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    margin: 0,
                    minWidth: 0,
                  },
                }}
              >
                <Typography
                  component="span"
                  onClick={(e): void => {
                    // 折りたたみの開閉に巻き込まれないようにする
                    e.stopPropagation();
                    void downloadTemplate(t);
                  }}
                  sx={{
                    fontSize: '14px',
                    color: '#1976d2',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {pendingReplaceOf(t)?.file.name ?? t.file_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '14px', color: gray80, whiteSpace: 'nowrap' }}>
                    {effectiveSheets(t).length}シート
                  </Typography>

                  {/* 差し替え。ドキュメントIDを保ったままファイルだけ入れ替える */}
                  <Tooltip
                    title={pendingReplaceOf(t) ? '差し替えを取り消す' : 'ファイルを差し替える'}
                  >
                    <span>
                      {pendingReplaceOf(t) ? (
                        <IconButton
                          onClick={(e): void => {
                            e.stopPropagation();
                            if (t.id) unstageReplace(t.id);
                          }}
                          aria-label="差し替えを取り消す"
                          sx={{ padding: '2px' }}
                        >
                          <UndoIcon sx={{ fontSize: '20px', color: gray60 }} />
                        </IconButton>
                      ) : (
                        <IconButton
                          component="label"
                          onClick={(e): void => e.stopPropagation()}
                          aria-label="ファイルを差し替える"
                          sx={{ padding: '2px' }}
                          disabled={loading}
                        >
                          <SwapHorizIcon sx={{ fontSize: '20px', color: gray60 }} />
                          <input
                            type="file"
                            accept=".xlsx"
                            hidden
                            onChange={(e): void => {
                              const selected = e.target.files?.[0];
                              if (selected) void handleFileSelect(selected, t);
                              // 同じファイルを選び直せるようにする
                              e.target.value = '';
                            }}
                          />
                        </IconButton>
                      )}
                    </span>
                  </Tooltip>

                  <Tooltip title="削除する">
                    {/* 開閉に巻き込まれないよう伝播を止める */}
                    <IconButton
                      onClick={(e): void => {
                        e.stopPropagation();
                        if (t.id) stageRemove(t.id);
                      }}
                      aria-label="フォーマットを削除する"
                      sx={{ padding: '2px' }}
                    >
                      <CancelIcon sx={{ fontSize: '22px', color: gray60 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ padding: '12px 0' }}>
                <Typography sx={{ fontSize: '12px', color: gray80, marginBottom: '8px' }}>
                  書き込む対象のシート（使わないシートはチェックを外してください）
                </Typography>
                {(sheetNamesOf(t) ?? []).map(name => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        checked={effectiveSheets(t).includes(name)}
                        onChange={(): void => {
                          if (!t.id) return;
                          const current = effectiveSheets(t);
                          stageSheetChange(
                            t.id,
                            current.includes(name)
                              ? current.filter(n => n !== name)
                              : [...current, name],
                          );
                        }}
                        sx={{ '&.Mui-checked': { color: orange50 } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: '14px', color: gray80 }}>{name}</Typography>}
                    sx={{ display: 'flex' }}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          ))}

          {/* 未保存の追加分。パターンを保存した時点で実際に登録される */}
          {pending.added.map(a => (
            <Accordion
              key={a.tempId}
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{ '&::before': { display: 'none' }, backgroundColor: 'transparent' }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                // 既定の <button> だと、中に置く差し替え・削除ボタンが
                // button の入れ子になって DOM として不正になるため div にする
                component="div"
                sx={{
                  padding: 0,
                  borderBottom: `1px solid ${gray40}`,
                  minHeight: '48px',
                  '& .MuiAccordionSummary-content': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    margin: 0,
                    minWidth: 0,
                  },
                }}
              >
                <Typography
                  component="span"
                  onClick={(e): void => {
                    // 折りたたみの開閉に巻き込まれないようにする
                    e.stopPropagation();
                    saveAsFile(a.file, a.file.name);
                  }}
                  sx={{
                    fontSize: '14px',
                    color: '#1976d2',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {a.file.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '14px', color: gray80, whiteSpace: 'nowrap' }}>
                    {a.targetSheets.length}シート
                  </Typography>
                  <Tooltip title="取り消す">
                    <IconButton
                      onClick={(e): void => {
                        e.stopPropagation();
                        unstageAdded(a.tempId);
                      }}
                      aria-label="追加を取り消す"
                      sx={{ padding: '2px' }}
                    >
                      <CancelIcon sx={{ fontSize: '22px', color: gray60 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ padding: '12px 0' }}>
                <Typography sx={{ fontSize: '12px', color: gray80, marginBottom: '8px' }}>
                  書き込む対象のシート（使わないシートはチェックを外してください）
                </Typography>
                {a.allSheets.map(name => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        checked={a.targetSheets.includes(name)}
                        onChange={(): void => {
                          const next = a.targetSheets.includes(name)
                            ? a.targetSheets.filter(n => n !== name)
                            : [...a.targetSheets, name];
                          if (next.length === 0) return;
                          applyPending(prev => ({
                            ...prev,
                            added: prev.added.map(x =>
                              x.tempId === a.tempId ? { ...x, targetSheets: next } : x,
                            ),
                          }));
                        }}
                        sx={{ '&.Mui-checked': { color: orange50 } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: '14px', color: gray80 }}>{name}</Typography>}
                    sx={{ display: 'flex' }}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ExportTemplateManager;
